import { useState, useMemo } from 'react';
import { Modifier, usePopper } from 'react-popper';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';
import { useDebugRerenders } from '@wix/da-hooks/pkg/useDebugRerenders';
import { convertFromPopperPlacement, convertToPopperPlacement } from '../helpers';
import type { PopperPlacement, PopperPositioningProps, PositionableProps } from '../types';
import sassVariables from '../../../styles/variables.scss';

const wrapperMaxWidth = parseInt(sassVariables['wrapper_max_width']);

export interface Props extends PopperPositioningProps {
  disablePlacementModifier?: boolean;
}

/** This is our base popper wrapper that applies our custom modifiers etc */
export function useBasePopper({
  disablePlacementModifier,
  placement,
  secondaryPlacement,
  referenceElement,
  positionFixed,
  eventsEnabled = true,
  offsetModifier: offsetModifierProp,
  allowFlip = true,
}: Props): PositionableProps {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const memoizedOffsetModifierProp = useDeepEqualMemo(offsetModifierProp);

  /* This modifier will force the popover to not be centered when it reaches
    the max width, as per designers request. By default it sets the `start`
    placement variant when that is the case, but if the popover doesn't fit
    the built-in `flip` modifier will kick in and position it accordingly. */
  const overridePlacement: Modifier<'overridePlacement'> = useMemo(
    () => ({
      name: 'overridePlacement',
      enabled: !disablePlacementModifier,
      phase: 'beforeMain',
      requiresIfExists: ['offsets', 'flip'],
      fn: ({ state }) => {
        const { width } = state.rects.popper;
        const maxWidth = disablePlacementModifier ? Number.POSITIVE_INFINITY : wrapperMaxWidth;
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const { placement, secondaryPlacement } = convertFromPopperPlacement(
          state.placement as PopperPlacement
        );
        if (width >= maxWidth && !secondaryPlacement) {
          state.placement = convertToPopperPlacement(placement, 'start');
          state.reset = true;
        }
      },
    }),
    [disablePlacementModifier]
  );

  const arrowModifier: Modifier<'arrow'> = useMemo(
    () => ({
      name: 'arrow',
      enabled: !!arrowElement,
      options: { element: arrowElement },
    }),
    [arrowElement]
  );

  const eventListenerModifier: Modifier<'eventListeners'> = useMemo(
    () => ({
      name: 'eventListeners',
      enabled: eventsEnabled,
      options: {
        scroll: true,
        resize: true,
      },
    }),
    [eventsEnabled]
  );

  const offsetModifier: Modifier<'offset'> = useMemo(
    () => ({
      name: 'offset',
      options: { offset: memoizedOffsetModifierProp },
    }),
    [memoizedOffsetModifierProp]
  );

  const flipModifier: Modifier<'flip'> = useMemo(
    () => ({
      name: 'flip',
      enabled: allowFlip,
    }),
    [allowFlip]
  );

  const memoizedModifiers = useDeepEqualMemo([
    overridePlacement,
    arrowModifier,
    eventListenerModifier,
    offsetModifier,
    flipModifier,
  ]);

  const { styles, attributes, update, state } = usePopper(referenceElement, popperElement, {
    placement: convertToPopperPlacement(placement, secondaryPlacement),
    strategy: positionFixed ? 'fixed' : 'absolute',
    modifiers: memoizedModifiers,
  });

  useDebugRerenders('useBasePopper', {
    disablePlacementModifier,
    placement,
    secondaryPlacement,
    referenceElement,
    positionFixed,
    eventsEnabled,
    offsetModifier,
    allowFlip,
    popperElement,
    arrowElement,
  });

  const { placement: effectivePlacement, secondaryPlacement: effectiveSecondaryPlacement } =
    convertFromPopperPlacement(state?.placement as PopperPlacement);

  return {
    rootRef: setPopperElement,
    arrowRef: setArrowElement,
    contentStyle: styles.popper,
    arrowStyle: styles.arrow,
    contentAttributes: attributes.popper,
    arrowAttributes: attributes.arrow,
    effectivePlacement,
    effectiveSecondaryPlacement,
    updatePositioning: update ?? undefined,
  };
}
